// The books (technical design §6.14; M7/M8; the accounting prototype): one place for the bookkeeper,
// the owner and the manager. A side list of the parts (on a phone, a row of tabs), each its own
// screen over the action layer: Overview, Banking, Receivables, Payables, Tax, Ledger & reports,
// Payroll, and the agents' money requests waiting for a person.

import { useEffect, useRef, useState } from 'react';
import { Header, type NavProps } from '../Nav.tsx';
import { booksPartsFor } from './parts.ts';
import { Approvals } from './Approvals.tsx';
import { Banking } from './Banking.tsx';
import { BankingPage } from './BankingPage.tsx';
import { ledgerReturnUntold } from './Connections.tsx';
import { Ledger } from './Ledger.tsx';
import { LedgerPage } from './LedgerPage.tsx';
import { CloseMonthPage } from './CloseMonthPage.tsx';
import { InvoicesPage } from './InvoicesPage.tsx';
import { PaymentsPage } from './PaymentsPage.tsx';
import { Overview } from './Overview.tsx';
import { Payables } from './Payables.tsx';
import { Payroll } from './Payroll.tsx';
import { Receivables } from './Receivables.tsx';
import { Setup } from './Setup.tsx';
import { Tax } from './Tax.tsx';
import { TaxFilingPage } from './TaxFilingPage.tsx';
import type { BooksPart, BooksProps } from './props.ts';
import { useToast } from './ui.tsx';
import { useDesign } from '../blueprint/design.ts';
import '@flowology/ui-web/base.css';
import './books.css';
import './blueprint.css';
// .bp-money's grid, which a customer's Money card (history/Customers.tsx) draws with too
import './money-lists.css';

export function BooksScreen({
  nav,
  held,
  open,
  onPart,
  onHours,
}: {
  nav: NavProps;
  /** The permissions the person holds (/auth/session). */
  held: readonly string[];
  /** A part to show, asked for from outside (a notification, a document link); `at` makes each ask a new one. */
  open?: { part: BooksPart; at: number; focus?: string };
  /** The part shown, each time it changes: the address follows it (#/books/banking). */
  onPart?: (part: BooksPart) => void;
  /** Blueprint: Payroll's timesheets open Hours on the period. */
  onHours?: (date: string) => void;
}) {
  const [part, setPart] = useState<BooksPart>(open?.part ?? (ledgerReturnUntold() ? 'ledger' : 'overview'));
  useEffect(() => {
    if (open) setPart(open.part);
  }, [open?.part, open?.at]);
  const [toast, setToast] = useToast();
  const props: BooksProps = {
    held,
    toast: setToast,
    go: setPart,
    ...(nav.timeZone ? { timeZone: nav.timeZone } : {}),
    ...(open?.focus && open.part === part ? { focus: open.focus } : {}),
    ...(onHours ? { onHours } : {}),
  };
  // Only the parts this role may read (QA-274): no Payroll for a manager, whose payPeriods.list is refused.
  // Blueprint's own parts (Invoices, Payments and refunds, Close a month) are in its rail alone
  const bp = useDesign() === 'blueprint';
  const parts = booksPartsFor(held, bp);
  const here = parts.find((p) => p.part === part) ?? parts[0]!;
  // in Blueprint the part is named as its rail names it, unless it has the prototype's question
  const title = bp ? (here.bp ?? here.rail) : here.title;
  const said = useRef(onPart);
  said.current = onPart;
  useEffect(() => said.current?.(here.part), [here.part]);
  // Blueprint's pages on ui-web's patterns (BL-189) draw their own title block, so none of Books' frame
  const tz = nav.timeZone ? { timeZone: nav.timeZone } : {};
  const shownToast = toast && (
    <div className="toast" role="status">
      {toast}
    </div>
  );
  if (bp && here.part === 'invoices') return <InvoicesPage nav={nav} held={held} />;
  if (bp && here.part === 'payments') return <PaymentsPage nav={nav} {...tz} />;
  if (bp && here.part === 'banking')
    return (
      <>
        <BankingPage nav={nav} held={held} toast={setToast} go={setPart} {...tz} />
        {shownToast}
      </>
    );
  if (bp && here.part === 'ledger')
    return (
      <>
        <LedgerPage
          key={props.focus ? `${props.focus}-${open?.at}` : 'ledger'}
          nav={nav}
          held={held}
          toast={setToast}
          go={setPart}
          {...tz}
          {...(props.focus ? { focus: props.focus } : {})}
        />
        {shownToast}
      </>
    );
  if (bp && here.part === 'close')
    return (
      <>
        <CloseMonthPage nav={nav} held={held} toast={setToast} go={setPart} {...tz} />
        {shownToast}
      </>
    );
  if (bp && here.part === 'tax')
    return (
      <>
        <TaxFilingPage nav={nav} toast={setToast} />
        {shownToast}
      </>
    );
  return (
    <div className="app books">
      <Header nav={nav} title={title} lede={here.lede} />
      <div className="books-body">
        {/* in Blueprint the Accounting rail lists the parts (blueprint/places.ts), so no list of its own */}
        {!bp && (
          <nav className="books-parts" aria-label="Books">
            {parts.map((p) => (
              <button
                key={p.part}
                className={`btn quiet${part === p.part ? ' on' : ''}`}
                aria-current={part === p.part ? 'page' : undefined}
                onClick={() => setPart(p.part)}
              >
                {p.label}
              </button>
            ))}
          </nav>
        )}
        <main className="books-main">
          {part === 'overview' && <Overview {...props} />}
          {part === 'banking' && <Banking {...props} />}
          {part === 'receivables' && <Receivables {...props} />}
          {part === 'payables' && <Payables {...props} />}
          {part === 'tax' && <Tax {...props} />}
          {part === 'ledger' && (
            <Ledger key={props.focus ? `${props.focus}-${open?.at}` : 'ledger'} {...props} />
          )}
          {here.part === 'payroll' && (
            <Payroll key={props.focus ? `${props.focus}-${open?.at}` : 'payroll'} {...props} />
          )}
          {part === 'approvals' && <Approvals {...props} />}
          {part === 'setup' && <Setup {...props} />}
        </main>
      </div>
      {shownToast}
    </div>
  );
}
